import { computed, inject } from '@angular/core';
import { Router } from '@angular/router';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import { AuthUserStore } from '@qtek/libs/auth-user';
import {
  ErrorSnackBarData,
  NormalSnackBarData,
  SnackBarService,
} from '@qtek/libs/snack-bar-core';
import {
  CompanyOutput,
  PersonOutput,
  RelUserModel,
  UserModel,
} from '@qtek/shared/models';
import { catchError, map, Observable, of, Subscription, take, tap } from 'rxjs';
import { MenuStore } from '../menu/menu.store';
import { PermissionsStore } from '../permission';
import { CompanyService } from './company.service';
import { PersonService } from './person.service';
import { UserStoreService } from './user.service';

interface UserAction {
  params: { sts: number; vndId: string };
}

export interface UserState {
  person: PersonOutput;
  company: CompanyOutput | null;
  user: RelUserModel | null;
  actions: UserAction[] | null;
}

const initialState: UserState = {
  person: null,
  company: null,
  user: null,
  actions: null,
};

export const UserStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withMethods(
    (
      store,
      userService = inject(UserStoreService),
      personService = inject(PersonService),
      companyService = inject(CompanyService),
      permissionsStore = inject(PermissionsStore),
      menuStore = inject(MenuStore),
      snackBar = inject(SnackBarService),
      authUserStore = inject(AuthUserStore),
      router = inject(Router)
    ) => {
      const mergeBitMasks = (prev: any, next: any): any => {
        if (!next) return prev;
        return {
          ...prev,
          ...next,
          _prgs: (prev?._prgs ?? 0) | (next._prgs ?? 0),
        };
      };

      const mapToUserState = (res: any): UserState =>
        res == null
          ? initialState
          : {
              person: res.person ?? null,
              company: res.company ?? null,
              user: res.user ?? null,
              actions: res.actions ?? null,
            };

      const extractCompany = (res: any): any => (res as any)?.company ?? res;

      let activeReloadSub: Subscription | null = null;

      const reloadUserAfterCompanyChange = (): void => {
        activeReloadSub?.unsubscribe();
        activeReloadSub = userService
          .getCurrentUser({ skipRevalidateErrorHandling: true })
          .pipe(
            take(1),
            tap(({ res: reloadedUser }) => {
              if (!reloadedUser) return;
              authUserStore.setUser(reloadedUser);
              if (reloadedUser.license?.features) {
                permissionsStore.setPermissions(reloadedUser.license.features);
              }
              patchState(store, mapToUserState(reloadedUser));
            }),
            catchError(() => of(null))
          )
          .subscribe();
      };

      const patchAuthUser = (
        override: Partial<Pick<UserModel, 'person' | 'company'>>
      ): void => {
        const currentUser = authUserStore.user();
        if (currentUser) {
          authUserStore.setUser({ ...currentUser, ...override });
        }
      };

      const mergeAndApplyPerson = (res: any): PersonOutput => {
        const newBitMasks = mergeBitMasks(
          store.person()?.bitMasks,
          res.bitMasks
        );
        const newPerson = { ...store.person(), ...res, bitMasks: newBitMasks };
        patchState(store, {
          person: newPerson,
          actions: res.actions ?? store.actions(),
        });
        return newPerson;
      };

      const mergeAndApplyCompany = (res: any): CompanyOutput | null => {
        const company = extractCompany(res);
        if (!company) return null;
        const newBitMasks = mergeBitMasks(
          store.company()?.bitMasks,
          company.bitMasks
        );
        const newCompany = {
          ...store.company(),
          ...company,
          bitMasks: newBitMasks,
        };
        patchState(store, { company: newCompany });
        return newCompany;
      };

      const applyCompanyUpdateSuccess = (
        res: any,
        skipReload: boolean
      ): void => {
        const newCompany = mergeAndApplyCompany(res);
        if (!newCompany) return;
        patchAuthUser({
          person: store.person(),
          company: newCompany,
        });
        snackBar.showSnackBar({
          type: 'NormalMessage',
          message: 'GNR_CMP_INFO_SAVED',
        } as NormalSnackBarData);
        if (!skipReload) {
          menuStore.loadMain();
          menuStore.loadSetup();
          reloadUserAfterCompanyChange();
        }
      };

      return {
        setUserData(data: any): void {
          patchState(store, mapToUserState(data));
        },

        getCurrentUser(payload: any = {}): Observable<boolean> {
          return userService.getCurrentUser(payload).pipe(
            tap(({ res }) => {
              authUserStore.setUser(res);
              if (res?.license?.features) {
                permissionsStore.setPermissions(res.license.features);
              } else {
                snackBar.showSnackBar({
                  type: 'ErrorMessage',
                  message: 'GNR_MISSED_PERMISSIONS',
                } as ErrorSnackBarData);
                router.navigate(['error']);
              }
              patchState(store, mapToUserState(res));
            }),
            map(() => true),
            catchError(() => of(false))
          );
        },

        updateCurrentUser(
          payload: Partial<PersonOutput>,
          personId: string
        ): Observable<boolean> {
          return personService.updatePerson(personId, payload).pipe(
            tap(({ res }) => {
              if (!res) return;
              const newPerson = mergeAndApplyPerson(res);
              patchAuthUser({
                person: newPerson,
                company: store.company(),
              });
              snackBar.showSnackBar({
                type: 'NormalMessage',
                message: 'GNR_PRS_INFO_SAVED',
              } as NormalSnackBarData);
            }),
            map(() => true),
            catchError(() => of(false))
          );
        },

        changePassword(
          password: string,
          password2: string
        ): Observable<boolean> {
          return userService.changePassword(password, password2).pipe(
            map(() => true),
            catchError(() => of(false))
          );
        },

        updateCurrentUserCompany(
          payload: { form?: any; additionalPayload?: any },
          skipReload = false
        ): Observable<boolean> {
          const companyId = store.company()?._id;
          return companyService
            .updateCompany(payload.form, companyId, payload.additionalPayload)
            .pipe(
              tap(({ res }) => applyCompanyUpdateSuccess(res, skipReload)),
              map(() => true),
              catchError(() => of(false))
            );
        },

        applyPersonData(res: any): void {
          if (!res) return;
          const newPerson = mergeAndApplyPerson(res);
          patchAuthUser({
            person: newPerson,
            company: store.company(),
          });
        },

        applyCompanyData(res: any): void {
          const newCompany = mergeAndApplyCompany(res);
          if (!newCompany) return;
          patchAuthUser({
            person: store.person(),
            company: newCompany,
          });
        },
      };
    }
  ),
  withComputed(({ person, company, user, actions }) => ({
    currentPerson: person,
    mainCompany: company,
    isAuthorized: computed(() => !!person()?.personId),
    personProgress: computed(() => person()?.bitMasks?._prgs || 0),
    companyProgress: computed(() => company()?.bitMasks?._prgs || 0),
    tutorialProgress: computed(() => person()?.bitMasks?._uitut),
    currentPersonLanguage: computed(() => person()?.language),
    relationType: computed(() => user()?.role ?? null),
    userRelation: user,
    isPersonalAccount: computed(
      () => !!(company() && (company()!.tp === 2000 || !company()!.tp))
    ),
    userActions: actions,
    currentPersonTz: computed(() => person()?.timezone),
  }))
);
